import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import MetaChip from "../../UIToolkit/Badges/MetaChip";
import type { HolidayGroup } from "./holidayApi";
import { holidayGroupById, holidayGroupColor, holidayGroupLabel } from "./holidayFormat";

type Props = {
	groups: readonly HolidayGroup[];
	/** Null = Not assigned. */
	groupId: string | null;
	/** What to say when there is no group ("Not assigned", "Choose group"). */
	emptyLabel?: string;
	size?: "small" | "medium";
	dataTestId?: string;
};

/** "Group A" in the group's colour, or a quiet neutral chip when there is no group. */
export const HolidayGroupChip: FC<Props> = ({ groups, groupId, emptyLabel, size = "small", dataTestId }) => {
	const { t } = useTranslation();
	const group = groupId === null ? undefined : holidayGroupById(groups, groupId);
	if (group === undefined) {
		// DEFAULT: most places show a person with no group as "Not assigned"; a holiday passes "Choose group".
		// An outline only, so a column full of people without a group stays quiet next to the coloured ones.
		return (
			<MetaChip
				label={emptyLabel ?? t("holidays.group.notAssigned")}
				customTone={{ ink: INK.MUTED, bg: SURFACE, border: NEUTRAL[300] }}
				size={size}
				dataTestId={dataTestId}
			/>
		);
	}
	const color = holidayGroupColor(groups, group.id);
	return (
		<MetaChip
			label={holidayGroupLabel(t, group)}
			customTone={{ ink: color.ink, bg: color.fill, border: color.dot }}
			size={size}
			dataTestId={dataTestId}
			sx={{ fontWeight: 600 }}
		/>
	);
};
